import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import toast from './utils/Toast';
import Loader from './utils/Loader';
import Navbar from './utils/Navbr';
import './style/AdminDashboard.css';

// --- Animation Component ---
const CountUp = ({ end, duration = 2000, prefix = '', suffix = '', decimals = 0 }) => {
    const [count, setCount] = useState(0);

    useEffect(() => {
        let startTime = null;
        const startValue = 0;
        const endValue = parseFloat(end) || 0;

        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = timestamp - startTime;
            const percentage = Math.min(progress / duration, 1);
            const ease = (x) => (x === 1 ? 1 : 1 - Math.pow(2, -10 * x));
            const current = startValue + (endValue - startValue) * ease(percentage);
            setCount(current);

            if (progress < duration) window.requestAnimationFrame(step);
        };
        window.requestAnimationFrame(step);
    }, [end, duration]);

    return <>{prefix}{count.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</>;
};

// --- Network Performance Graph ---
const PerformanceGraph = ({ data }) => {
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const graphRef = useRef(null);

    if (!data || data.length === 0) return (
        <div className="graph-card-unique center-placeholder">
            <p>Collecting Network Data...</p>
        </div>
    );

    const width = 800;
    const height = 300;
    const padding = 40;

    const maxRev = Math.max(...data.map(d => d.revenue)) || 100;
    const maxPayout = Math.max(...data.map(d => d.payouts)) || 100;
    const maxProfit = Math.max(...data.map(d => d.profit)) || 100;

    const globalMax = Math.max(maxRev, maxPayout, maxProfit) * 1.1;

    const getY = (val) => height - padding - ((val / globalMax) * (height - (padding * 2)));
    const getX = (index) => padding + (index * ((width - (padding * 2)) / (data.length - 1)));

    const generatePath = (key) => {
        let d = `M ${getX(0)} ${getY(data[0][key])}`;
        for (let i = 0; i < data.length - 1; i++) {
            const x_mid = (getX(i) + getX(i + 1)) / 2;
            const y_curr = getY(data[i][key]);
            const y_next = getY(data[i + 1][key]);
            d += ` C ${x_mid} ${y_curr}, ${x_mid} ${y_next}, ${getX(i + 1)} ${y_next}`;
        }
        return d;
    };

    return (
        <div className="graph-card-unique" ref={graphRef}>
            <div className="graph-header-fixed">
                <h3 className="graph-title-unique">Network Financial Performance</h3>
                <div className="legend-unique">
                    <span className="leg-item-unique rev"><span className="dot"></span> Revenue</span>
                    <span className="leg-item-unique pay"><span className="dot"></span> Payouts</span>
                    <span className="leg-item-unique prof"><span className="dot"></span> Profit</span>
                </div>
            </div>

            <div className="graph-scroll-container">
                <svg viewBox={`0 0 ${width} ${height}`} className="custom-svg-unique">
                    {[...Array(5)].map((_, i) => (
                        <line key={i} x1={padding} y1={padding + (i * ((height - 2 * padding) / 4))} x2={width - padding} y2={padding + (i * ((height - 2 * padding) / 4))} stroke="rgba(255,255,255,0.05)" />
                    ))}

                    <path d={generatePath('revenue')} fill="none" stroke="#3b82f6" strokeWidth="3" className="path-anim" />
                    <path d={generatePath('payouts')} fill="none" stroke="#ef4444" strokeWidth="3" className="path-anim delay-1" />
                    <path d={generatePath('profit')} fill="none" stroke="#10b981" strokeWidth="3" className="path-anim delay-2" />

                    {data.map((item, i) => (
                        <g key={i}>
                            <rect
                                x={getX(i) - 20} y="0" width="40" height={height}
                                fill="transparent"
                                onMouseEnter={() => setHoveredPoint({ ...item, x: getX(i) })}
                                onMouseLeave={() => setHoveredPoint(null)}
                                style={{ cursor: 'pointer' }}
                            />
                            <circle cx={getX(i)} cy={getY(item.revenue)} r="4" fill="#3b82f6" className="graph-dot" />
                            <circle cx={getX(i)} cy={getY(item.payouts)} r="4" fill="#ef4444" className="graph-dot" />
                            <circle cx={getX(i)} cy={getY(item.profit)} r="4" fill="#10b981" className="graph-dot" />
                            <text x={getX(i)} y={height - 10} fill="#a1a1aa" fontSize="12" textAnchor="middle">{item.day}</text>
                        </g>
                    ))}
                </svg>

                {hoveredPoint && (
                    <div
                        className="graph-tooltip-unique"
                        style={{ left: `${(hoveredPoint.x / width) * 100}%` }}
                    >
                        <div className="tooltip-header">{hoveredPoint.day}</div>
                        <div className="tooltip-row rev">Rev: <span>₹{hoveredPoint.revenue}</span></div>
                        <div className="tooltip-row pay">Pay: <span>₹{hoveredPoint.payouts}</span></div>
                        <div className="tooltip-row prof">Profit: <span>₹{hoveredPoint.profit}</span></div>
                    </div>
                )}
            </div>
        </div>
    );
};

// --- Main Admin Dashboard ---
const AdminDashboard = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [adminData, setAdminData] = useState({
        adminName: '',
        stats: {
            publishers: { total: 0, active: 0 },
            revenue: { total: 0, today: 0 },
            payouts: { pendingCount: 0, pendingAmount: 0 },
            offers: { active: 0, total: 0 }
        },
        graphData: [],
        topPublishers: [],
        topOffers: [],
        recentActivity: []
    });

    const getCookie = (name) => {
        const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    };

    const fetchAdminData = async () => {
        const adminToken = getCookie('adminToken');

        try {
            const response = await axios.post('https://publisher.cashbolt.in.net/server/', {
                action: 'fetchAdminDashboard',
                adminToken: adminToken || 'demo_token'
            });

            if (response.data.status === true) {
                setAdminData(response.data.data);
            } else {
                console.warn("API Error, loading Mock Data");
                if (response.data.message) toast.error(response.data.message);
            }
        } catch (error) {
            console.error("Network Error, loading Mock Data", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const adminToken = getCookie('adminToken');

        const init = async () => {
            try {
                // ✅ 1. Admin Auth Check
                const authRes = await axios.post('https://publisher.cashbolt.in.net/server/', {
                    action: 'checkAuthAdmin',
                    adminToken
                });

                if (!authRes.data.status) {
                    toast.error(authRes.data.message || 'Admin session expired');
                    navigate('/cbMediaAdmin7/login');
                    return;
                }

                // ✅ 2. If Auth OK → Fetch Dashboard Data
                fetchAdminData();

            } catch (err) {
                toast.error('Admin auth check failed');
                navigate('/cbMediaAdmin7/login');
            }
        };

        init();   
    }, []);

    if (loading) return <Loader />;

    return (
        <div className="dashboard-wrapper-unique admin-theme">
            <Navbar name={adminData.adminName || 'Admin'} role="Administrator" isAdmin={true} />

            <div className="bg-shape-dash shape-d1"></div>
            <div className="bg-shape-dash shape-d2"></div>

            <div className="dashboard-content-unique">
                <div className="dash-header-unique">
                    <h2>Admin <span className="highlight-text">Control Panel</span></h2>
                    <p className="dash-date">System Overview • {new Date().toDateString()}</p>
                </div>

                {/* --- 1. Stats Grid --- */}
                <div className="stats-grid-unique">
                    {/* Publishers */}
                    <div className="stat-card-unique">
                        <div className="stat-icon-box blue">👥</div>
                        <div className="stat-info">
                            <h3>Publishers</h3>
                            <div className="stat-row">
                                <div className="stat-item">
                                    <span>Total Users</span>
                                    <strong><CountUp end={adminData.stats.publishers.total} /></strong>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>Active Now</span>
                                    <strong className="success-text"><CountUp end={adminData.stats.publishers.active} /></strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Revenue */}
                    <div className="stat-card-unique">
                        <div className="stat-icon-box green">💰</div>
                        <div className="stat-info">
                            <h3>Network Revenue</h3>
                            <div className="stat-row">
                                <div className="stat-item">
                                    <span>Total Gross</span>
                                    <strong className="earn-text"><CountUp end={adminData.stats.revenue.total} prefix="₹" decimals={0} /></strong>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>Today</span>
                                    <strong className="earn-text"><CountUp end={adminData.stats.revenue.today} prefix="₹" decimals={0} /></strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Payouts */}
                    <div className="stat-card-unique alert-border">
                        <div className="stat-icon-box red">⚠️</div>
                        <div className="stat-info">
                            <h3>Pending Payouts</h3>
                            <div className="stat-row">
                                <div className="stat-item">
                                    <span>Requests</span>
                                    <strong><CountUp end={adminData.stats.payouts.pendingCount} /></strong>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>Amount</span>
                                    <strong className="danger-text"><CountUp end={adminData.stats.payouts.pendingAmount} prefix="₹" /></strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Offers */}
                    <div className="stat-card-unique">
                        <div className="stat-icon-box purple">🏷️</div>
                        <div className="stat-info">
                            <h3>Campaigns</h3>
                            <div className="stat-row">
                                <div className="stat-item">
                                    <span>Active</span>
                                    <strong><CountUp end={adminData.stats.offers.active} /></strong>
                                </div>
                                <div className="stat-separator"></div>
                                <div className="stat-item">
                                    <span>Total</span>
                                    <strong><CountUp end={adminData.stats.offers.total} /></strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* --- 2. Graph --- */}
                <PerformanceGraph data={adminData.graphData} />

                {/* --- 3. Split Section (Publishers & Activity) --- */}
                <div className="split-section-unique">

                    {/* Top Publishers */}
                    <div className="dash-section-unique">
                        <h3 className="section-title-unique">🏆 Top Performing Publishers</h3>
                        <div className="trending-list-unique">
                            {adminData.topPublishers.map((pub, index) => (
                                <div key={pub.id} className="offer-row-unique">
                                    <div className="rank-badge">{index + 1}</div>
                                    <div className="offer-left">
                                        <div className="offer-meta">
                                            <h4>{pub.name}</h4>
                                            <span>ID: {pub.id}</span>
                                        </div>
                                    </div>
                                    <div className="offer-stats">
                                        <div className="os-item">
                                            <span>Revenue</span>
                                            <strong className="earn-text">₹{pub.revenue.toLocaleString()}</strong>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Recent Activity */}
                    <div className="dash-section-unique">
                        <h3 className="section-title-unique">⚡ Recent System Activity</h3>
                        <div className="activity-feed">
                            {adminData.recentActivity.map((log, index) => (
                                <div key={index} className="log-item">
                                    <div className={`log-dot ${log.type}`}></div>
                                    <div className="log-content">
                                        <p>{log.message}</p>
                                        <span>{log.time}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- 4. Top Performing Offers (Full Width Below Split) --- */}
                <div className="dash-section-unique full-width-section-unique">
                    <h3 className="section-title-unique">🔥 Top Performing Offers</h3>
                    <div className="trending-list-unique">
                        {adminData.topOffers && adminData.topOffers.map((offer) => (
                            <div key={offer.id} className="offer-row-unique">
                                {/* Logo & Name */}
                                <div className="offer-left offer-left-wide">
                                    <img src={offer.logo} alt="" className="offer-logo-small" />
                                    <div className="offer-meta">
                                        <h4>{offer.name}</h4>
                                        <span>ID: {offer.id}</span>
                                    </div>
                                </div>

                                {/* Stats Grid for Offer */}
                                <div className="offer-stats-grid">
                                    <div className="os-item">
                                        <span>Clicks</span>
                                        <strong>{offer.clicks.toLocaleString()}</strong>
                                    </div>
                                    <div className="os-item">
                                        <span>Conversions</span>
                                        <strong className="success-text">{offer.conversions.toLocaleString()}</strong>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
};

export default AdminDashboard;